// Origin resolution and bounded fetch, shared by every network call the
// widget makes.
//
// Both live here because both were previously duplicated per call site and
// drifted: the session path honoured the build-time origin while the
// widget-config path hardcoded production, so a non-production build minted
// sessions against one origin and read its config from another.

import { versionHeaders } from "./version.js";

declare global {
  // eslint-disable-next-line no-var
  var __SPEECHIFY_API_BASE__: string;
}

const DEFAULT_API_BASE = "https://api.speechify.ai";

const SLASH = 47;

/** Milliseconds before an unanswered request is abandoned. */
export const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Resolve the API origin: an explicit override wins, then the build-time
 * constant, then production. Trailing slashes are trimmed so callers can
 * concatenate a path unconditionally.
 */
export function resolveApiBase(override?: string | null): string {
  const base =
    override ||
    (typeof globalThis.__SPEECHIFY_API_BASE__ === "string" ? globalThis.__SPEECHIFY_API_BASE__ : DEFAULT_API_BASE);
  // Scanned back rather than stripped with /\/+$/, which backtracks
  // polynomially on a long run of slashes. The origin is caller-supplied
  // (the `api-base` attribute), so the input is not ours to trust.
  let end = base.length;
  while (end > 0 && base.charCodeAt(end - 1) === SLASH) end--;
  return base.slice(0, end);
}

/** Raised when a request exceeds REQUEST_TIMEOUT_MS, distinguishable from an abort. */
export class TimeoutError extends Error {
  constructor(url: string) {
    super(`Request timed out after ${REQUEST_TIMEOUT_MS}ms: ${url}`);
    this.name = "TimeoutError";
  }
}

/**
 * fetch bounded by a timeout and an optional caller signal.
 *
 * Hand-rolled rather than `AbortSignal.any` + `AbortSignal.timeout`, which
 * are too new to assume on a script that runs on arbitrary customer sites.
 * Without a bound, a black-holed connection (corporate proxy, DNS sinkhole,
 * captive portal) leaves the promise pending for the browser's own TCP
 * timeout — minutes — with the widget stuck mid-connect and no way out.
 *
 * Every request gets the widget's SDK-identity headers. Stamping them here
 * rather than per call site is why they cannot drift: this is the only
 * function in the widget that reaches the network.
 */
export async function fetchWithTimeout(url: string, init: RequestInit = {}, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(versionHeaders())) {
    headers.set(name, value);
  }

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  const onCallerAbort = () => controller.abort();
  signal?.addEventListener("abort", onCallerAbort);

  try {
    return await fetch(url, { ...init, headers, signal: controller.signal });
  } catch (err) {
    if (timedOut) throw new TimeoutError(url);
    throw err;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
}
